
import React, { createContext, useCallback, useEffect, useState } from "react";
import "../css/Layout.css";
import { Link, useLocation } from "react-router-dom";

export const LastUpdatedContext = createContext(null);

const DeviceLayout = ({ children, lastUpdatedOverride = null }) => {
  const API_BASE = "https://api.cloud.linkbms.eu";
  const location = useLocation();
  const [description, setDescription] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [newDescription, setNewDescription] = useState("");
  const [lastUpdated, setLastUpdated] = useState(null);
  const [, setTick] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(interval);
  }, []);

  const notifyDataUpdated = useCallback(() => {
    const id = getDeviceIdFromUrl();
    if (!id) return;

    fetch(`${API_BASE}/device/data?device_id=${id}`, {
      credentials: "include",
    })
      .then((response) => {
        if (!response.ok) throw new Error("Failed to fetch latest device data");
        return response.json();
      })
      .then((rows) => {
        if (Array.isArray(rows) && rows.length > 0 && rows[0].received_at) {
          const ts = Date.parse(rows[0].received_at);
          setLastUpdated(Number.isNaN(ts) ? null : ts);
        }
      })
      .catch((error) => {
        console.error("Error fetching last device timestamp:", error);
      });
  }, []);

  const effectiveLastUpdated = lastUpdatedOverride ?? lastUpdated;

  const getRelativeTime = (ts) => {
    const seconds = Math.floor((Date.now() - ts) / 1000);
    if (seconds < 5) return "just now";
    if (seconds < 60) return `${seconds}s ago`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    return `${hours}h ago`;
  };

  const fetchData = async () => {
    const id = getDeviceIdFromUrl();
    if (id) {
      try {
        const response = await fetch(`${API_BASE}/device/desc/${id}`, {
          credentials: "include",
        });
        const data = await response.json();
        setDescription(data.description || "N/A");
        setNewDescription(data.description);
        notifyDataUpdated();
      } catch (error) {
        console.error("Error fetching device data:", error);
      }
    } else {
      console.error("Device ID not found in the URL.");
    }
  };

  const getDeviceIdFromUrl = () => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get("id");
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      notifyDataUpdated();
    }, 30000);
    return () => clearInterval(interval);
  }, [notifyDataUpdated]);

  const handleEditClick = () => {
    setIsEditing(true);
  };

  const handleInputChange = (e) => {
    setNewDescription(e.target.value);
  };

  const handleUpdateDescription = async () => {
    const id = getDeviceIdFromUrl();
    try {
      const response = await fetch(`${API_BASE}/update_user_description`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          device_id: id,
          user_description: newDescription,
        }),
        credentials: "include",
      });

      if (response.ok) {
        const data = await response.json();
        setDescription(newDescription);
        setIsEditing(false);
        alert(data.message);
      } else {
        const errorData = await response.json();
        alert(errorData.error);
      }
    } catch (error) {
      console.error("Error updating description:", error);
    }
  };

  const searchParams = new URLSearchParams(location.search);
  const currentId = searchParams.get("id");

  return (
    <LastUpdatedContext.Provider value={notifyDataUpdated}>
      <div className="frame">
        <div className="topdevice">
          <div className="frame-buttons">
            <Link to={`/device/data?id=${currentId}`} className="frame-button">
              Info
            </Link>
            <Link to={`/device/detail?id=${currentId}`} className="frame-button">
              Detail
            </Link>
            <Link to={`/device/history?id=${currentId}`} className="frame-button">
              History
            </Link>
            <Link to={`/device/events?id=${currentId}`} className="frame-button">
              Events
            </Link>
          </div>
          <div className="frame-description">
            {isEditing ? (
              <div>
                <input
                  type="text"
                  value={newDescription}
                  onChange={handleInputChange}
                  placeholder="Enter new description"
                />
                <button onClick={handleUpdateDescription}>Save</button>
                <button onClick={() => setIsEditing(false)}>Cancel</button>
              </div>
            ) : (
              <div>
                <div className="Title" id="Title" onClick={handleEditClick}>
                  {description}
                </div>
              </div>
            )}
          </div>
          {effectiveLastUpdated ? (
            <div className="frame-last-updated">
              {`Updated: ${getRelativeTime(effectiveLastUpdated)}`}
            </div>
          ) : null}
        </div>
        {children}
      </div>
    </LastUpdatedContext.Provider>
  );
};

export default DeviceLayout;